import { Target, Eye, Sparkles, ArrowUpRight } from "lucide-react";

const ABOUT_IMG =
  "https://images.pexels.com/photos/6572481/pexels-photo-6572481.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const objectives = [
  {
    title: "Confianza Comercial",
    text: "Construimos relaciones basadas en la transparencia financiera y el respeto absoluto a los acuerdos de inventario con nuestros proveedores y clientes.",
  },
  {
    title: "Flexibilidad Operativa",
    text: "Nos adaptamos con rapidez a las fluctuaciones de la demanda y a las necesidades específicas de volumen de cada punto de venta.",
  },
  {
    title: "Eficiencia Logística",
    text: "Optimizamos los tiempos de traslado, recepción y colocación de mercancías para asegurar que el stock esté siempre disponible en el momento exacto.",
  },
  {
    title: "Equidad en el Intercambio",
    text: "Diseñamos esquemas de negocio donde la ganancia es proporcional y compartida, impulsando el crecimiento conjunto de toda la cadena de suministro.",
  },
];

const Nosotros = () => {
  return (
    <section
      id="nosotros"
      data-testid="nosotros-section"
      className="bg-white text-black py-24 lg:py-32 relative"
    >
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12">
        {/* Section label */}
        <div className="flex items-center gap-4 mb-16">
          <div className="h-px w-12 bg-[#C7343D]" />
          <span className="text-xs font-semibold tracking-[0.3em] uppercase text-[#C7343D]">
            Nosotros
          </span>
        </div>

        {/* Headline + image */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 mb-24">
          <div className="lg:col-span-7">
            <h2
              data-testid="nosotros-heading"
              className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-light tracking-[-0.03em] leading-[1] text-black text-balance"
            >
              Somos una empresa{" "}
              <span className="italic font-extralight text-[#595959]">
                dinámica y versátil
              </span>{" "}
              dedicada al comercio al por mayor.
            </h2>
            <div className="mt-10 max-w-2xl space-y-6 text-[#595959] text-lg leading-relaxed font-light">
              <p>
                En Distribuidora Sacaru, somos el enlace estratégico que dinamiza el mercado, especializándonos en la intermediación comercial de alto alcance al por mayor y menor. Nos distinguimos por nuestra flexibilidad operativa, conectando eficientemente a productores líderes con los canales de venta más lucrativos mediante esquemas de distribución tradicionales y modelos de consignación. Nuestra pasión es eliminar las barreras de entrada para nuevos productos, garantizando un flujo constante de mercancías y una colocación inteligente que maximiza las ventas.
              </p>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative h-[400px] lg:h-full min-h-[400px] overflow-hidden border border-black/10">
              <img
                src={ABOUT_IMG}
                alt="Operaciones logísticas Sacaru"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 to-transparent">
                <div className="text-white text-xs uppercase tracking-[0.25em]">
                  Operaciones · Guadalajara
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mission & Vision */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/10 border border-black/10 mb-24">
          <div className="bg-white p-10 lg:p-14 bento-card">
            <div className="flex items-center gap-3 mb-6">
              <Target size={20} className="text-[#C7343D]" />
              <span className="text-xs uppercase tracking-[0.3em] font-semibold text-[#C7343D]">
                Nuestra Misión
              </span>
            </div>
            <p className="font-display text-2xl lg:text-3xl text-black leading-snug tracking-tight font-light text-balance">
              Facilitar el éxito comercial de nuestros socios y clientes mediante soluciones de intermediación ágiles, seguras y de mutuo beneficio, garantizando el abasto oportuno y la rotación eficiente de productos en el mercado.
            </p>
          </div>
          <div className="bg-[#F7F7F7] p-10 lg:p-14 bento-card">
            <div className="flex items-center gap-3 mb-6">
              <Eye size={20} className="text-[#F3901A]" />
              <span className="text-xs uppercase tracking-[0.3em] font-semibold text-[#F3901A]">
                Nuestra Visión
              </span>
            </div>
            <p className="font-display text-2xl lg:text-3xl text-black leading-snug tracking-tight font-light text-balance">
              Consolidarnos para el 2030 como la red de distribución intermedia más confiable y diversificada de Jalisco, liderando la modernización del comercio por consignación mediante tecnología y transparencia logística.
            </p>
          </div>
        </div>

        {/* Objectives */}
        <div>
          <div className="flex items-center gap-4 mb-10">
            <Sparkles size={18} className="text-[#C7343D]" />
            <span className="text-xs uppercase tracking-[0.3em] font-semibold text-black">
              Valores
            </span>
          </div>

          <div className="border-t border-black/10">
            {objectives.map((obj, idx) => (
              <div
                key={obj.title}
                data-testid={`objective-${idx}`}
                className="group grid grid-cols-12 gap-6 border-b border-black/10 py-8 lg:py-10 hover:bg-[#F7F7F7] transition-colors"
              >
                <div className="col-span-2 md:col-span-1 numeric text-[#595959] text-sm">
                  0{idx + 1}
                </div>
                <div className="col-span-10 md:col-span-5">
                  <h3 className="font-display text-2xl lg:text-3xl text-black font-normal tracking-tight">
                    {obj.title}
                  </h3>
                </div>
                <div className="col-span-12 md:col-span-5 text-[#595959] leading-relaxed">
                  {obj.text}
                </div>
                <div className="col-span-12 md:col-span-1 flex md:justify-end items-start">
                  <ArrowUpRight
                    size={22}
                    className="text-black opacity-30 group-hover:opacity-100 group-hover:text-[#C7343D] transition-all"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Nosotros;
